import { useEffect } from 'react';
import { App, Form, InputNumber, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { ratesQueryKeys, useCreateRate, useUpdateRate } from '@/features/agreements/hooks/useRates';
import { useCreateRuleGroup, useUpdateRuleGroup, useDeleteRuleGroup, useGetRuleGroups, ruleGroupsQueryKeys } from '@/features/agreements/hooks/useRuleGroups';
import { RulesDrawer } from '../RulesDrawer';
import { validateGroup } from '../RulesDrawer/ruleGroupUtils';

const { Text } = Typography;

export const CreateRateDrawer = ({ open, onClose, agreementId, planId, rate, onSuccess }) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const isEditMode = !!rate;

  const createRateMutation = useCreateRate();
  const updateRateMutation = useUpdateRate();
  const createRuleGroupMutation = useCreateRuleGroup();
  const updateRuleGroupMutation = useUpdateRuleGroup();
  const deleteRuleGroupMutation = useDeleteRuleGroup();

  const ruleGroupsQuery = useGetRuleGroups(
    { rateId: rate?.id },
    { enabled: open && !!rate?.id }
  );
  const existingRuleGroup = (ruleGroupsQuery.data ?? [])[0] ?? null;

  useEffect(() => {
    if (open) {
      if (isEditMode && rate) {
        form.setFieldsValue({
          percentage: rate.percentage,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, isEditMode, rate, form]);

  const handleSave = async ({ ruleGroupPayload, ruleGroupId }) => {
    const values = await form.validateFields();
    const percentage = values.percentage;
    let rateIdForRules = null;

    if (isEditMode) {
      await updateRateMutation.mutateAsync({
        agreementId,
        planId,
        rateId: rate.id,
        data: { percentage },
      });
      notification.success({
        message: <Text strong>Tasa actualizada</Text>,
        description: '¡La tasa se ha actualizado correctamente!',
      });

      rateIdForRules = rate.id;
      const hasValidRules = validateGroup(ruleGroupPayload);
      if (ruleGroupId != null) {
        if (hasValidRules) {
          await updateRuleGroupMutation.mutateAsync({
            id: ruleGroupId,
            data: ruleGroupPayload,
          });
        } else {
          await deleteRuleGroupMutation.mutateAsync({ id: ruleGroupId });
        }
      } else if (hasValidRules) {
        await createRuleGroupMutation.mutateAsync({
          data: {
            ...ruleGroupPayload,
            rateId: rate.id,
          },
        });
      }
    } else {
      const newRate = await createRateMutation.mutateAsync({
        agreementId,
        planId,
        data: { percentage },
      });
      rateIdForRules = newRate.id;
      notification.success({
        message: <Text strong>Tasa creada</Text>,
        description: '¡La tasa se ha creado correctamente!',
      });

      if (validateGroup(ruleGroupPayload)) {
        await createRuleGroupMutation.mutateAsync({
          data: {
            ...ruleGroupPayload,
            rateId: newRate.id,
          },
        });
      }
    }

    void queryClient.invalidateQueries({
      queryKey: ratesQueryKeys.list({ agreementId, planId }),
    });
    if (rateIdForRules) {
      void queryClient.invalidateQueries({
        queryKey: ruleGroupsQueryKeys.list({ rateId: rateIdForRules }),
      });
    }
    form.resetFields();
    onClose?.();
    onSuccess?.();
  };

  const handleClose = () => {
    form.resetFields();
    onClose?.();
  };

  const rateFormContent = (
    <Form form={form} layout='vertical'>
      <Form.Item
        label='Valor de tasa'
        name='percentage'
        rules={[{ required: true }]}
      >
        <InputNumber
          placeholder='Ej: 3.49'
          min={0}
          max={100}
          step={0.01}
          formatter={(value) => (value ? `${value}%` : '')}
          parser={(value) => (value ? value.replace('%', '') : '')}
          style={{ width: '100%' }}
        />
      </Form.Item>
    </Form>
  );

  return (
    <RulesDrawer
      open={open}
      onClose={handleClose}
      title={isEditMode ? 'Editar tasa' : 'Crear nueva tasa'}
      description='Estas reglas determinan cuándo debe aplicarse esta tasa específica durante una cotización.'
      extraContent={rateFormContent}
      ruleGroup={existingRuleGroup}
      onSave={handleSave}
      submitButtonText={isEditMode ? 'Actualizar tasa' : 'Crear tasa'}
      width='48em'
      rulesRequired={false}
    />
  );
};
